import React, { Component } from 'react'
import { toast } from 'react-toastify'
import { getString } from '../../../locale'
import { getUserSecurity, lockSystemNow, setUserSecurity } from '../../../api/0x0005/system'

type Props = {
    clientId: string
}

type State = {
    loading: boolean
    saving: boolean
    lockOnStartup: boolean
    userPin: string
}

export default class UserSecurityTab extends Component<Props, State> {
    state: State = {
        loading: true,
        saving: false,
        lockOnStartup: false,
        userPin: '',
    }

    async componentDidMount() {
        await this.load()
    }

    load = async () => {
        try {
            this.setState({ loading: true })
            const response = await getUserSecurity(this.props.clientId)
            this.setState({ lockOnStartup: response.data.lockOnStartup === 1 })
        } catch (error: any) {
            toast.error(error?.message || getString('system.user_security_save_error'))
        } finally {
            this.setState({ loading: false })
        }
    }

    save = async () => {
        const pin = this.state.userPin.trim()
        if (pin.length > 0 && !/^\d{6}$/.test(pin)) {
            toast.error(getString('system.user_pin_invalid'))
            return
        }
        try {
            this.setState({ saving: true })
            const response = await setUserSecurity(this.props.clientId, this.state.lockOnStartup ? 1 : 0, pin)
            if (response.data.success === 1) {
                toast.success(getString('system.user_security_saved'))
                this.setState({ userPin: '' })
                await this.load()
            } else {
                toast.error(response.data.errorMsg || getString('system.user_security_save_error'))
            }
        } catch (error: any) {
            toast.error(error?.message || getString('system.user_security_save_error'))
        } finally {
            this.setState({ saving: false })
        }
    }

    lockNow = async () => {
        try {
            const response = await lockSystemNow(this.props.clientId)
            if (response.data.success === 1) {
                toast.success(getString('system.lock_now_success'))
            } else {
                toast.error(response.data.errorMsg || getString('system.lock_now_error'))
            }
        } catch (error: any) {
            toast.error(error?.message || getString('system.lock_now_error'))
        }
    }

    render() {
        const { loading, saving, lockOnStartup, userPin } = this.state
        if (loading) {
            return (
                <div className="flex items-center justify-center h-64">
                    <div className="text-lg text-gray-600 dark:text-gray-400">{getString('common.loading')}</div>
                </div>
            )
        }

        return (
            <div className="p-6 space-y-6 bg-white dark:bg-gray-900">
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6">
                    <h2 className="text-xl font-bold mb-4 text-gray-800 dark:text-white">{getString('system.user_security')}</h2>
                    <div className="bg-white dark:bg-gray-700 rounded-lg p-4 shadow-sm space-y-3">
                        <label className="flex items-center justify-between text-sm text-gray-700 dark:text-gray-300">
                            <span>{getString('system.lock_on_startup')}</span>
                            <input
                                type="checkbox"
                                checked={lockOnStartup}
                                onChange={e => this.setState({ lockOnStartup: e.target.checked })}
                                className="h-5 w-5"
                            />
                        </label>
                        <input
                            type="password"
                            inputMode="numeric"
                            maxLength={6}
                            value={userPin}
                            onChange={e => this.setState({ userPin: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                            placeholder={getString('system.user_pin_placeholder')}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                        <button
                            onClick={this.save}
                            disabled={saving}
                            className="w-full px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 font-semibold transition-colors disabled:opacity-70"
                        >
                            {getString('system.user_security_save')}
                        </button>
                        {lockOnStartup && (
                            <button
                                onClick={this.lockNow}
                                className="w-full px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 font-semibold transition-colors"
                            >
                                {getString('system.lock_now')}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        )
    }
}
